import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Fab,
  IconButton,
  Grid,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Settings,
  Add,
  Edit,
  Delete,
  SwapVert,
  Inventory,
  EventBusy,
  Link as LinkIcon,
  Block,
} from '@mui/icons-material'
import RuleForm from '../components/rules/RuleForm'
import { usePlantContext } from '../hooks/usePlantContext'
import { api } from '../services/api'

const RULE_TYPES = [
  { key: 'sequencing', label: 'Sequencing', icon: SwapVert },
  { key: 'minQuantity', label: 'Min Quantity', icon: Inventory },
  { key: 'capReservation', label: 'Cap Reservation', icon: EventBusy },
  { key: 'itemPairing', label: 'Item Pairing', icon: LinkIcon },
  { key: 'exclusion', label: 'Exclusion', icon: Block },
]

// Fallback demo data used when API returns empty
const DEMO_RULES = {
  sequencing: [
    {
      id: 'seq-1',
      name: 'Light-to-Dark Color Sequencing',
      description: 'Schedule light-colored products before dark-colored products on each line to minimize changeover cleaning time.',
      lines: ['Line 1', 'Line 2', 'Line 3', 'Line 4'],
      sortOrder: 'light-to-dark',
      groupBy: 'Brite Code',
      active: true,
    },
    {
      id: 'seq-2',
      name: 'Pack-Size Grouping',
      description: 'Group same pack sizes together within a brite code run to reduce changeover.',
      lines: ['Line 1', 'Line 2'],
      sortOrder: 'ascending pack size',
      groupBy: 'Pack Size',
      active: true,
    },
  ],
  minQuantity: [
    {
      id: 'min-1',
      name: 'Min Brite Run — 120k Cans',
      description: 'Every brite code run must produce at least 120,000 cans (10,000 cases × 12 cans/case) to justify changeover costs.',
      scope: 'All Brite Codes',
      threshold: 120000,
      unit: 'cans',
      groupBy: 'Brite Code',
      active: true,
    },
    {
      id: 'min-2',
      name: 'Min Line Run — 4 Hours',
      description: 'No production run should be shorter than 4 hours to ensure efficient startup and shutdown.',
      scope: 'All Lines',
      threshold: 4,
      unit: 'hours',
      groupBy: 'Line',
      active: true,
    },
  ],
  capReservation: [
    {
      id: 'cap-1',
      name: 'Co-Pack Buffer — Line 4',
      description: 'Reserve 24 hours/month on Line 4 for co-pack overflow and special orders.',
      line: 'Line 4',
      startMonth: 'Apr 2026',
      endMonth: 'Mar 2027',
      reservedHours: 24,
      active: true,
    },
  ],
  itemPairing: [
    {
      id: 'pair-1',
      name: 'MG Chili + MG TX Mexican Pairing',
      description: 'When scheduling MG CHILI BNS, always schedule MG TX MX CHILI in the same run to share brite prep.',
      itemA: 'MG CHILI BNS 12/15',
      itemB: 'MG TX MX CHILI BN 12/15',
      active: true,
    },
  ],
  exclusion: [
    {
      id: 'excl-1',
      name: 'Allergen Exclusion — Brite 12221 / 12321',
      description: 'Do not schedule Brite 12221 and 12321 back-to-back without an allergen clean (8h gap required).',
      excludeA: '12221',
      excludeB: '12321',
      active: false,
    },
  ],
}

const API_TYPE_MAP = {
  sequencing_advisory: 'sequencing',
  minimum_quantity_warning: 'minQuantity',
  capacity_reservation: 'capReservation',
  item_pairing: 'itemPairing',
  exclusion: 'exclusion',
}

function groupRulesByType(rulesArray) {
  const grouped = {}
  for (const rt of RULE_TYPES) grouped[rt.key] = []
  for (const rule of rulesArray) {
    const key = API_TYPE_MAP[rule.type] || rule.type || 'sequencing'
    if (grouped[key]) grouped[key].push(rule)
    else if (grouped.sequencing) grouped.sequencing.push(rule)
  }
  return grouped
}

function RuleCard({ rule, typeKey, onEdit, onDelete }) {
  const TypeIcon = RULE_TYPES.find((t) => t.key === typeKey)?.icon || Settings

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <TypeIcon sx={{ fontSize: 20, color: 'primary.main', mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1">{rule.name}</Typography>
            <Chip
              label={rule.active ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                mt: 0.5,
                backgroundColor: rule.active ? 'success.light' : 'surface.containerHigh',
                color: rule.active ? 'success.dark' : 'text.secondary',
                fontWeight: 600,
                fontSize: '0.65rem',
              }}
            />
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {rule.description}
        </Typography>

        {rule.threshold && (
          <Chip
            label={`≥ ${rule.threshold.toLocaleString()} ${rule.unit}`}
            size="small"
            variant="outlined"
            sx={{ mt: 1.5 }}
          />
        )}
        {rule.reservedHours && (
          <Chip
            label={`${rule.reservedHours}h/month reserved`}
            size="small"
            variant="outlined"
            sx={{ mt: 1.5 }}
          />
        )}
        {rule.lines && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
            {rule.lines.map((l) => (
              <Chip key={l} label={l} size="small" variant="outlined" />
            ))}
          </Box>
        )}
      </CardContent>
      <CardActions sx={{ px: 2, pb: 1.5 }}>
        <Tooltip title="Edit rule">
          <IconButton size="small" onClick={() => onEdit(rule)}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete rule">
          <IconButton size="small" onClick={() => onDelete(rule.id)} color="error">
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  )
}

export default function Rules() {
  const { plant } = usePlantContext()
  const [tab, setTab] = useState(0)
  const [rules, setRules] = useState(DEMO_RULES)
  const [formOpen, setFormOpen] = useState(false)
  const [editRule, setEditRule] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    api.get(`/rules?plant=${plant.plantCode}`)
      .then((data) => {
        if (cancelled) return
        if (Array.isArray(data) && data.length > 0) {
          setRules(groupRulesByType(data))
        } else {
          // Fallback: use demo data when API returns empty
          setRules(DEMO_RULES)
        }
      })
      .catch((err) => {
        if (cancelled) return
        setError(err.message || 'Failed to load rules')
        // Fallback: keep demo data on error
        setRules(DEMO_RULES)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [plant.plantCode])

  const activeType = RULE_TYPES[tab]
  const typeRules = rules[activeType.key] || []

  const totalRuleCount = Object.values(rules).reduce((sum, arr) => sum + arr.length, 0)

  const handleEdit = (rule) => {
    setEditRule(rule)
    setFormOpen(true)
  }

  const handleDelete = async (ruleId) => {
    try {
      await api.delete(`/rules/${ruleId}`)
    } catch {
      // soft-delete locally even if API fails
    }
    setRules((prev) => ({
      ...prev,
      [activeType.key]: prev[activeType.key].filter((r) => r.id !== ruleId),
    }))
  }

  const handleSave = async (ruleData) => {
    try {
      if (editRule) {
        const updated = await api.put(`/rules/${editRule.id}`, { ...ruleData, plant: plant.plantCode })
        setRules((prev) => {
          const typeKey = ruleData.type
          const existing = prev[typeKey] || []
          return {
            ...prev,
            [typeKey]: existing.map((r) => (r.id === editRule.id ? { ...r, ...updated, ...ruleData } : r)),
          }
        })
      } else {
        const created = await api.post('/rules', { ...ruleData, plant: plant.plantCode })
        setRules((prev) => {
          const typeKey = ruleData.type
          const existing = prev[typeKey] || []
          return {
            ...prev,
            [typeKey]: [
              ...existing,
              { ...ruleData, ...created, id: created?.id || `${typeKey}-${Date.now()}`, active: true },
            ],
          }
        })
      }
    } catch {
      // Optimistic local update as fallback
      setRules((prev) => {
        const typeKey = ruleData.type
        const existing = prev[typeKey] || []
        if (editRule) {
          return {
            ...prev,
            [typeKey]: existing.map((r) => (r.id === editRule.id ? { ...r, ...ruleData } : r)),
          }
        }
        return {
          ...prev,
          [typeKey]: [
            ...existing,
            { ...ruleData, id: `${typeKey}-${Date.now()}`, active: true },
          ],
        }
      })
    }
    setEditRule(null)
  }

  const handleAddNew = () => {
    setEditRule(null)
    setFormOpen(true)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Settings color="primary" sx={{ fontSize: 22 }} />
        <Typography variant="h3" sx={{ flex: 1 }}>Custom Rules</Typography>
        <Typography variant="body2" color="text.secondary">
          {totalRuleCount} rules · {plant.name}
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {RULE_TYPES.map((rt) => {
          const count = (rules[rt.key] || []).length
          return (
            <Tab
              key={rt.key}
              icon={<rt.icon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={`${rt.label} (${count})`}
              sx={{ minHeight: 48, textTransform: 'none' }}
            />
          )
        })}
      </Tabs>

      {typeRules.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <activeType.icon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            No {activeType.label.toLowerCase()} rules yet
          </Typography>
          <Button variant="outlined" startIcon={<Add />} sx={{ mt: 2 }} onClick={handleAddNew}>
            Add {activeType.label} Rule
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {typeRules.map((rule) => (
            <Grid key={rule.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <RuleCard
                rule={rule}
                typeKey={activeType.key}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Fab
        color="primary"
        onClick={handleAddNew}
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
      >
        <Add />
      </Fab>

      <RuleForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditRule(null) }}
        onSave={handleSave}
        ruleType={activeType.key}
        editRule={editRule}
      />
    </Box>
  )
}
